
import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/base/Button';
import Input from '../../components/base/Input';
import Select from '../../components/base/Select';
import Modal from '../../components/base/Modal';

interface Sale {
  id: string;
  date: string;
  automatId: string;
  automatName: string;
  productId: string;
  productName: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  profit: number;
}

interface SaleStats {
  todaySales: number;
  todayRevenue: number;
  todayProfit: number;
  totalSales: number;
  totalRevenue: number;
  totalProfit: number;
}

interface Automat {
  id: string;
  name: string;
}

interface Product {
  id: string;
  name: string;
  sellPrice: number;
  purchasePrice: number;
  automatPrices?: { [automatId: string]: number }; // Automatspezifische Preise hinzufügen
}

export default function Verkauf() {
  const { user } = useAuth();
  const [sales, setSales] = useState<Sale[]>([]);
  const [automaten, setAutomaten] = useState<Automat[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<SaleStats>({
    todaySales: 0,
    todayRevenue: 0,
    todayProfit: 0,
    totalSales: 0,
    totalRevenue: 0,
    totalProfit: 0
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    automatId: '',
    productId: '',
    quantity: ''
  });

  // Load data from Supabase
  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user]);

  const loadData = async () => {
    try {
      // Load sales
      const { data: salesData, error: salesError } = await supabase
        .from('daten')
        .select('*')
        .eq('user_id', user?.id)
        .eq('data_type', 'sale')
        .order('created_at', { ascending: false });

      if (salesError) throw salesError;

      const salesList = salesData.map(item => ({
        id: item.id,
        ...item.data
      }));

      setSales(salesList);

      // Load automaten - KORRIGIERT: Verbesserte Filterung für echte Automaten
      const { data: automatenData, error: automatenError } = await supabase
        .from('daten')
        .select('*')
        .eq('user_id', user?.id)
        .eq('data_type', 'automat');

      if (automatenError) throw automatenError;

      // ERWEITERT: Filtere nur echte Automaten-Maschinen (nicht Referenzen)
      const automatenList = automatenData
        .filter(item => {
          // Nur echte Automaten-Maschinen verwenden
          return item.data && item.data.machine_type === 'physical_automat';
        })
        .map(item => ({
          id: item.id,
          name: item.data.name,
          status: item.data.status || 'aktiv'
        }))
        .filter(automat => automat.status !== 'gelöscht'); // Filtere gelöschte Automaten aus

      console.log('🤖 Geladene echte Automaten für Verkauf (physical machines):', automatenList.length);
      console.log('🚫 Referenzen werden korrekt ausgeschlossen');
      setAutomaten(automatenList);

      // Load products from warehouse - ERWEITERT: Automatspezifische Preise laden
      const { data: productsData, error: productsError } = await supabase
        .from('daten')
        .select('*')
        .eq('user_id', user?.id)
        .eq('data_type', 'product');

      if (productsError) throw productsError;

      const productsList = productsData
        .filter(item => item.data && item.data.name && item.data.sellPrice)
        .map(item => ({
          id: item.id,
          name: item.data.name,
          sellPrice: item.data.sellPrice,
          purchasePrice: item.data.purchasePrice || 0,
          automatPrices: item.data.automatPrices || {} // Automatspezifische Preise laden
        }));

      console.log('📦 Geladene Produkte mit automatspezifischen Preisen:', productsList);
      setProducts(productsList);

    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Neue Funktion: Effektiven Preis für Automat-Produkt-Kombination ermitteln
  const getEffectivePrice = (productId: string, automatId: string) => {
    const product = products.find(p => p.id === productId);
    if (!product) return 0;

    // Prüfe ob automatspezifischer Preis existiert
    if (product.automatPrices && product.automatPrices[automatId]) {
      console.log(`💰 Verwende automatspezifischen Preis für ${product.name} in Automat ${automatId}: CHF${product.automatPrices[automatId]}`);
      return product.automatPrices[automatId];
    }

    // Fallback auf Standard-Verkaufspreis
    console.log(`💰 Verwende Standard-Verkaufspreis für ${product.name}: CHF${product.sellPrice}`);
    return product.sellPrice;
  };

  // Calculate statistics based on sales
  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    const todaySales = sales.filter(sale => sale.date === today);
    
    const newStats: SaleStats = {
      todaySales: todaySales.length,
      todayRevenue: todaySales.reduce((sum, sale) => sum + sale.totalPrice, 0),
      todayProfit: todaySales.reduce((sum, sale) => sum + sale.profit, 0),
      totalSales: sales.length,
      totalRevenue: sales.reduce((sum, sale) => sum + sale.totalPrice, 0),
      totalProfit: sales.reduce((sum, sale) => sum + sale.profit, 0)
    };
    
    setStats(newStats);
  }, [sales]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const selectedAutomat = automaten.find(a => a.id === formData.automatId);
    const selectedProduct = products.find(p => p.id === formData.productId);
    const quantity = parseInt(formData.quantity);
    
    if (!selectedAutomat || !selectedProduct || !quantity) return;
    
    // KORREKTUR: Verwende automatspezifischen Preis wenn vorhanden
    const effectivePrice = getEffectivePrice(selectedProduct.id, selectedAutomat.id);
    const totalPrice = effectivePrice * quantity;
    const profit = (effectivePrice - selectedProduct.purchasePrice) * quantity;
    
    console.log(`🔄 Verkauf wird erstellt:`, {
      produkt: selectedProduct.name,
      automat: selectedAutomat.name,
      standardPreis: selectedProduct.sellPrice,
      effektiverPreis: effectivePrice,
      menge: quantity,
      gesamtpreis: totalPrice,
      gewinn: profit
    });
    
    const saleData = {
      date: formData.date,
      automatId: selectedAutomat.id,
      automatName: selectedAutomat.name,
      productId: selectedProduct.id,
      productName: selectedProduct.name,
      quantity: quantity,
      unitPrice: effectivePrice, // Verwende effektiven Preis
      totalPrice: totalPrice,
      profit: profit
    };

    try {
      // 1. Verkauf in Datenbank speichern
      const { data, error } = await supabase
        .from('daten')
        .insert({
          user_id: user?.id,
          data_type: 'sale',
          data: saleData
        })
        .select()
        .single();

      if (error) throw error;

      // 2. NEUE FUNKTION: Lagerbestand automatisch reduzieren
      console.log(`📦 Reduziere Lagerbestand für Produkt ${selectedProduct.name} um ${quantity} Stück`);
      
      // Aktuelles Produkt aus dem Lager laden
      const { data: currentProductData, error: productError } = await supabase
        .from('daten')
        .select('*')
        .eq('id', selectedProduct.id)
        .eq('user_id', user?.id)
        .eq('data_type', 'product')
        .single();

      if (productError) {
        console.error('Fehler beim Laden des Produkts:', productError);
        throw new Error('Produkt konnte nicht gefunden werden');
      }

      // Neuen Bestand berechnen
      const currentStock = currentProductData.data.currentStock || 0;
      const newStock = Math.max(0, currentStock - quantity); // Verhindere negative Bestände
      
      console.log(`📊 Bestandsänderung: ${currentStock} → ${newStock} (${quantity} verkauft)`);

      // Lagerbestand in Datenbank aktualisieren
      const updatedProductData = {
        ...currentProductData.data,
        currentStock: newStock
      };

      const { error: updateError } = await supabase
        .from('daten')
        .update({ data: updatedProductData })
        .eq('id', selectedProduct.id)
        .eq('user_id', user?.id);

      if (updateError) {
        console.error('Fehler beim Aktualisieren des Lagerbestands:', updateError);
        // Verkauf trotzdem erfassen, aber Warnung anzeigen
        alert(`⚠️ Verkauf erfasst, aber Lagerbestand konnte nicht aktualisiert werden. Bitte prüfen Sie das Lager manuell.`);
      } else {
        console.log(`✅ Lagerbestand erfolgreich aktualisiert: ${selectedProduct.name} → ${newStock} Stück`);
      }

      // 3. Verkaufsliste aktualisieren
      setSales(prev => [{
        id: data.id,
        ...saleData
      }, ...prev]);

      // 4. Produktliste aktualisieren (für die Anzeige)
      setProducts(prev => prev.map(p => 
        p.id === selectedProduct.id 
          ? { ...p, currentStock: newStock }
          : p
      ));

      setIsModalOpen(false);
      setFormData({
        date: new Date().toISOString().split('T')[0],
        automatId: '',
        productId: '',
        quantity: ''
      });
      
      // Erfolgreiche Meldung mit Bestandsinfo
      const stockWarning = newStock <= (currentProductData.data.minStock || 0) 
        ? `\n⚠️ ACHTUNG: Mindestbestand erreicht! (${newStock}/${currentProductData.data.minStock || 0})` 
        : '';
      
      alert(`✅ Verkauf erfolgreich erfasst!\n\n💰 Verwendeter Preis: CHF${effectivePrice.toFixed(2)}\n📦 Neuer Lagerbestand: ${newStock} Stück${stockWarning}`);
      
    } catch (error) {
      console.error('Error adding sale:', error);
      alert('Fehler beim Erfassen des Verkaufs: ' + (error as Error).message);
    }
  };

  const handleDeleteSale = async (saleId: string, saleName: string) => {
    if (!confirm(`Sind Sie sicher, dass Sie den Verkauf "${saleName}" löschen möchten?`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('daten')
        .delete()
        .eq('id', saleId)
        .eq('user_id', user?.id);

      if (error) throw error;

      setSales(prev => prev.filter(sale => sale.id !== saleId));
      alert('Verkauf erfolgreich gelöscht!');
    } catch (error) {
      console.error('Error deleting sale:', error);
      alert('Fehler beim Löschen des Verkaufs.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-line text-4xl text-blue-600 animate-spin mb-4"></i>
          <p className="text-gray-600">Lade Verkaufsdaten...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full overflow-x-hidden">
      <div className="w-full max-w-full overflow-x-hidden">
        <div className="bg-white rounded-lg shadow-sm border w-full overflow-x-hidden">
          <div className="px-3 sm:px-4 lg:px-6 py-4 border-b border-gray-200">
            <div className="flex flex-col space-y-3 sm:space-y-0 sm:flex-row sm:justify-between sm:items-center">
              <div className="min-w-0 flex-1">
                <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 flex items-center">
                  Verkaufs-Verwaltung
                  <span className="ml-3 text-sm text-blue-600">
                    <i className="ri-cloud-line mr-1"></i>
                    Supabase
                  </span>
                </h1>
                <p className="text-gray-600 mt-1 text-sm sm:text-base break-words">
                  Erfassen und verwalten Sie Ihre Verkäufe ({sales.length} Verkäufe)
                </p>
              </div>
              <button
                onClick={() => setIsModalOpen(true)}
                className="w-full sm:w-auto bg-blue-600 text-white px-4 py-3 sm:py-2 rounded-md hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center flex-shrink-0"
              >
                <i className="ri-add-line w-4 h-4 flex items-center justify-center mr-2"></i>
                Neuen Verkauf erfassen
              </button>
            </div>
          </div>

          {/* Statistiken Dashboard */}
          <div className="px-3 sm:px-4 lg:px-6 py-6 border-b border-gray-200 bg-gray-50">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Heute */}
              <div className="bg-white p-6 rounded-lg shadow-sm border">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Heute</h3>
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <i className="ri-calendar-line text-blue-600 w-5 h-5 flex items-center justify-center"></i>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Verkäufe:</span>
                    <span className="text-lg font-bold text-gray-900">{stats.todaySales}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Umsatz:</span>
                    <span className="text-lg font-bold text-green-600">CHF{stats.todayRevenue.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Gewinn:</span>
                    <span className="text-lg font-bold text-blue-600">CHF{stats.todayProfit.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Gesamt */}
              <div className="bg-white p-6 rounded-lg shadow-sm border">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Gesamt</h3>
                  <div className="p-2 bg-green-100 rounded-lg">
                    <i className="ri-line-chart-line text-green-600 w-5 h-5 flex items-center justify-center"></i>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Verkäufe:</span>
                    <span className="text-lg font-bold text-gray-900">{stats.totalSales}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Umsatz:</span>
                    <span className="text-lg font-bold text-green-600">CHF{stats.totalRevenue.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Gewinn:</span>
                    <span className="text-lg font-bold text-blue-600">CHF{stats.totalProfit.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Performance */}
              <div className="bg-white p-6 rounded-lg shadow-sm border">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Performance</h3>
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <i className="ri-trophy-line text-purple-600 w-5 h-5 flex items-center justify-center"></i>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Ø Verkaufswert:</span>
                    <span className="text-lg font-bold text-gray-900">
                      CHF{stats.totalSales > 0 ? (stats.totalRevenue / stats.totalSales).toFixed(2) : '0.00'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Ø Gewinn/Verkauf:</span>
                    <span className="text-lg font-bold text-green-600">
                      CHF{stats.totalSales > 0 ? (stats.totalProfit / stats.totalSales).toFixed(2) : '0.00'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Gewinnmarge:</span>
                    <span className="text-lg font-bold text-blue-600">
                      {stats.totalRevenue > 0 ? ((stats.totalProfit / stats.totalRevenue) * 100).toFixed(1) : '0'}%
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="p-3 sm:p-4 lg:p-6 w-full overflow-x-hidden">
            {sales.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-gray-400 mb-4">
                  <i className="ri-shopping-cart-line text-6xl w-16 h-16 flex items-center justify-center mx-auto"></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-700 mb-2">
                  Keine Verkäufe vorhanden
                </h3>
                <p className="text-gray-500">
                  Erfassen Sie Ihren ersten Verkauf, um zu beginnen.
                </p>
              </div>
            ) : (
              <div>
                <div className="mb-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Verkaufs-Historie</h2>
                </div>
                
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse bg-white rounded-lg shadow-sm border">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                          Datum
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                          Automat
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                          Produkt
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                          Menge
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                          Einzelpreis
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                          Gesamtpreis
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                          Gewinn
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                          Aktionen
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {sales.map((sale) => (
                        <tr key={sale.id} className="hover:bg-gray-50">
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                            {new Date(sale.date).toLocaleDateString('de-DE')}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="p-1 bg-blue-100 rounded mr-2">
                                <i className="ri-robot-line text-blue-600 w-4 h-4 flex items-center justify-center"></i>
                              </div>
                              <span className="text-sm text-gray-900">{sale.automatName}</span>
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                            {sale.productName}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                            {sale.quantity}x
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                            CHF{sale.unitPrice.toFixed(2)}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                            CHF{sale.totalPrice.toFixed(2)}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-blue-600">
                            CHF{sale.profit.toFixed(2)}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm">
                            <div className="flex space-x-2">
                              <button className="text-gray-400 hover:text-blue-600 cursor-pointer">
                                <i className="ri-edit-line w-4 h-4 flex items-center justify-center"></i>
                              </button>
                              <button 
                                className="text-gray-400 hover:text-red-600 cursor-pointer"
                                onClick={() => handleDeleteSale(sale.id, `${sale.productName} (${new Date(sale.date).toLocaleDateString('de-DE')})`)}
                                title="Verkauf löschen"
                              >
                                <i className="ri-delete-bin-line w-4 h-4 flex items-center justify-center"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal für neuen Verkauf */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Neuen Verkauf erfassen"
        maxWidth="lg"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Verkaufsdatum"
            type="date"
            value={formData.date}
            onChange={(e) => handleInputChange('date', e.target.value)}
            required
          />
          
          <Select
            label="Automat auswählen"
            options={automaten.map(automat => ({ 
              value: automat.id, 
              label: automat.name 
            }))}
            value={formData.automatId}
            onChange={(value) => handleInputChange('automatId', value.toString())}
            placeholder="Automat auswählen..."
            required
          />
          
          <Select
            label="Produkt auswählen"
            options={products.map(product => {
              // Zeige den effektiven Preis basierend auf ausgewähltem Automaten
              const effectivePrice = formData.automatId ? 
                getEffectivePrice(product.id, formData.automatId) : 
                product.sellPrice;
              
              const priceDisplay = formData.automatId && 
                product.automatPrices && 
                product.automatPrices[formData.automatId] && 
                product.automatPrices[formData.automatId] !== product.sellPrice
                ? `CHF${effectivePrice.toFixed(2)} (spez. Preis)`
                : `CHF${effectivePrice.toFixed(2)}`;
              
              return { 
                value: product.id, 
                label: `${product.name} - ${priceDisplay}`
              };
            })}
            value={formData.productId}
            onChange={(value) => handleInputChange('productId', value.toString())}
            placeholder="Produkt auswählen..."
            required
          />
          
          <Input
            label="Verkaufte Menge"
            type="number"
            min="1"
            value={formData.quantity}
            onChange={(e) => handleInputChange('quantity', e.target.value)}
            placeholder="1"
            required
          />
          
          {/* Verkaufsvorschau - KORRIGIERT: Zeige effektiven Preis */}
          {formData.productId && formData.quantity && formData.automatId && (
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">Verkaufsvorschau:</h4>
              <div className="space-y-1 text-sm">
                {(() => {
                  const product = products.find(p => p.id === formData.productId);
                  const effectivePrice = getEffectivePrice(formData.productId, formData.automatId);
                  const isSpecialPrice = product?.automatPrices && 
                    product.automatPrices[formData.automatId] && 
                    product.automatPrices[formData.automatId] !== product.sellPrice;
                  
                  return (
                    <>
                      <div className="flex justify-between">
                        <span>Einzelpreis:</span>
                        <span className={isSpecialPrice ? 'text-orange-600 font-medium' : ''}>
                          CHF{effectivePrice.toFixed(2)}
                          {isSpecialPrice && <span className="text-xs ml-1">(automatspezifisch)</span>}
                        </span>
                      </div>
                      {isSpecialPrice && product && (
                        <div className="flex justify-between text-xs text-gray-500">
                          <span>Standard-Preis:</span>
                          <span>CHF{product.sellPrice.toFixed(2)}</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span>Menge:</span>
                        <span>{formData.quantity}x</span>
                      </div>
                      <div className="flex justify-between font-medium">
                        <span>Gesamtpreis:</span>
                        <span className="text-green-600">
                          CHF{(effectivePrice * parseInt(formData.quantity || '0')).toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between font-medium">
                        <span>Gewinn:</span>
                        <span className="text-blue-600">
                          CHF{((effectivePrice - (product?.purchasePrice || 0)) * parseInt(formData.quantity || '0')).toFixed(2)}
                        </span>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
          )}
          
          <div className="flex space-x-3 pt-4">
            <Button type="submit" className="flex-1">
              Verkauf erfassen
            </Button>
            <Button 
              type="button" 
              variant="secondary" 
              onClick={() => setIsModalOpen(false)}
            >
              Abbrechen
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
